import { supabase } from '@/integrations/supabase/client';
import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';

/**
 * Row shape returned by list/assign calls. Matches the `public.plan_documents`
 * table 1:1 — kept hand-written because we don't want to block on a full
 * `npm run gen:types` cycle after the migration just landed.
 */
export interface PlanDocumentRow {
  id: string;
  plan_id: string;
  document_message_id: string;
  document_session_id: string;
  user_id: string;
  position: number;
  created_at: string;
}

/**
 * Hydrated view used by the Plan Detail sidebar "Documents" section. Joins the
 * raw `plan_documents` row with the document's current title/snippet derived
 * from `ai_messages.parts[]`.
 */
export interface AssignedDocument {
  id: string;
  planId: string;
  messageId: string;
  sessionId: string;
  position: number;
  assignedAt: string;
  title: string;
  snippet: string;
  wordCount: number;
  type: 'blog' | 'document' | string;
}

interface GenerateDocumentOutput {
  title?: string;
  type?: string;
  markdown?: string;
}

function stripHeadingMarkers(markdown: string): string {
  return markdown
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^[-*_]{3,}$/gm, '')
    .replace(/\*\*|__|~~|\*|_|`/g, '')
    .trim();
}

function buildSnippet(markdown: string, maxChars = 80): string {
  const clean = stripHeadingMarkers(markdown).replace(/\s+/g, ' ');
  return clean.length > maxChars ? `${clean.slice(0, maxChars).trim()}…` : clean;
}

function countWords(markdown: string): number {
  const clean = stripHeadingMarkers(markdown);
  if (!clean) return 0;
  return clean.split(/\s+/).filter(Boolean).length;
}

function extractDocumentPart(parts: unknown): GenerateDocumentOutput | null {
  if (!Array.isArray(parts)) return null;
  for (const p of parts) {
    if (
      p &&
      typeof p === 'object' &&
      (p as { type?: string }).type === 'tool-generate_document' &&
      (p as { state?: string }).state === 'output-available' &&
      (p as { output?: unknown }).output &&
      typeof (p as { output: unknown }).output === 'object'
    ) {
      return (p as { output: GenerateDocumentOutput }).output;
    }
  }
  return null;
}

/**
 * List documents assigned to a plan, ordered by position then assignment time.
 * Returns `AssignedDocument[]` with hydrated title/snippet/word-count.
 */
export async function listByPlan(planId: string, userId: string): Promise<AssignedDocument[]> {
  if (!planId || !userId) return [];

  // Tenant scoping (2026-04-24): plan_documents RLS resolves via
  // current_account_owner_id(). Keep the explicit .eq('user_id', ...) filter
  // (required for the query planner to use the index — RLS alone still forces
  // a Seq Scan, verified via EXPLAIN ANALYZE) but re-target it at the
  // resolved account owner id so members see rows owned by their workspace
  // owner instead of matching nothing against their own raw id.
  const ownerId = await resolveOwnerId(userId);
  const { data: rows, error } = await supabase
    .from('plan_documents')
    .select('id, plan_id, document_message_id, document_session_id, position, created_at')
    .eq('plan_id', planId)
    .eq('user_id', ownerId)
    .order('position', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) {
    console.error('[PlanDocumentsService.listByPlan]', error);
    return [];
  }

  const joinRows = (rows ?? []) as Array<
    Pick<
      PlanDocumentRow,
      'id' | 'plan_id' | 'document_message_id' | 'document_session_id' | 'position' | 'created_at'
    >
  >;
  if (joinRows.length === 0) return [];

  const messageIds = joinRows.map((r) => r.document_message_id);
  // ai_messages is author-private (RLS: user_id = auth.uid(), reverted
  // 2026-07-10 — conversations must not be team-shared). plan_documents
  // stays owner-scoped/shared, so a document attached to a shared plan by
  // one team member will NOT hydrate its title/snippet for a different
  // viewer: this query returns nothing for a message it didn't author. This
  // is the accepted, known tradeoff of author-private conversations — the
  // document's underlying content has no home outside its originating
  // message until it is explicitly promoted into a shared artifact surface.
  // No client-side filter is added here on purpose: RLS alone (author-only)
  // is exactly the intended scoping for this table.
  const { data: messages, error: msgErr } = await supabase
    .from('ai_messages')
    .select('id, parts')
    .in('id', messageIds);

  if (msgErr) {
    console.error('[PlanDocumentsService.listByPlan] fetching messages', msgErr);
    return [];
  }

  const byMessageId = new Map<string, GenerateDocumentOutput>();
  for (const m of (messages ?? []) as Array<{ id: string; parts: unknown }>) {
    const output = extractDocumentPart(m.parts);
    if (output && typeof output.markdown === 'string') {
      byMessageId.set(m.id, output);
    }
  }

  const hydrated: AssignedDocument[] = [];
  for (const r of joinRows) {
    const output = byMessageId.get(r.document_message_id);
    if (!output || typeof output.markdown !== 'string') continue;
    const title =
      typeof output.title === 'string' && output.title.trim()
        ? output.title.trim()
        : output.type === 'blog'
          ? 'Untitled blog'
          : 'Untitled document';
    hydrated.push({
      id: r.id,
      planId: r.plan_id,
      messageId: r.document_message_id,
      sessionId: r.document_session_id,
      position: r.position,
      assignedAt: r.created_at,
      title,
      snippet: buildSnippet(output.markdown),
      wordCount: countWords(output.markdown),
      type: typeof output.type === 'string' ? output.type : 'document',
    });
  }
  return hydrated;
}

/**
 * Attach a document to a plan. Idempotent: violating the UNIQUE constraint
 * returns the existing row rather than erroring.
 */
export async function assign(params: {
  planId: string;
  documentMessageId: string;
  documentSessionId: string;
  userId: string;
}): Promise<{ ok: true; row: PlanDocumentRow } | { ok: false; error: string }> {
  const { planId, documentMessageId, documentSessionId, userId } = params;

  // Write must target the account owner's id — plan_documents with_check
  // requires user_id = current_account_owner_id(); a team member's own raw
  // session id would fail the RLS insert check.
  const ownerId = await resolveOwnerId(userId);
  const { data, error } = await supabase
    .from('plan_documents')
    .upsert(
      {
        plan_id: planId,
        document_message_id: documentMessageId,
        document_session_id: documentSessionId,
        user_id: ownerId,
      },
      { onConflict: 'plan_id,document_message_id', ignoreDuplicates: false },
    )
    .select()
    .single();

  if (error) {
    console.error('[PlanDocumentsService.assign]', error);
    return { ok: false, error: error.message };
  }
  return { ok: true, row: data as PlanDocumentRow };
}

/**
 * Detach a document from a plan by plan_documents.id.
 */
export async function unassign(
  id: string,
  userId: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  if (!id || !userId) return { ok: false, error: 'missing arguments' };

  // Defense-in-depth `.eq('user_id', ...)` must use the resolved account
  // owner id — otherwise a non-owner team member's own id never matches
  // (0 rows deleted, silent false-success) even though RLS would legitimately
  // allow the delete.
  const ownerId = await resolveOwnerId(userId);
  const { error } = await supabase
    .from('plan_documents')
    .delete()
    .eq('id', id)
    .eq('user_id', ownerId);
  if (error) {
    console.error('[PlanDocumentsService.unassign]', error);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

export const planDocumentsService = { listByPlan, assign, unassign };
